import React from "react";
import "../../styles/modal.css";
import {
  exclusiveSkipsConvergence,
  EMPTY_CONVERGE_FIELDS,
  gatewayRequiresConvergence,
} from "../../utils/gatewayConvergence";

/**
 * Gateway Configuration Modal - Enhanced with Wizard Mode
 * Allows users to configure BPMN gateways with branches, conditions, and convergence points.
 * Features:
 * - Step-by-step wizard for easier configuration
 * - Gateway presets for common patterns
 * - Visual branch builder with drag reordering
 * - Live validation and helpful tooltips
 * Supports: EXCLUSIVE, PARALLEL, INCLUSIVE, EVENT_BASED gateway types.
 */

const GATEWAY_TYPES = [
  {
    value: "EXCLUSIVE",
    label: "Exclusive (XOR)",
    symbol: "✕",
    color: "#ef4444",
    description: "Only ONE path executes based on condition",
    example: "If approved → continue, else → reject",
  },
  {
    value: "PARALLEL",
    label: "Parallel (AND)",
    symbol: "+",
    color: "#22c55e",
    description: "ALL paths execute simultaneously",
    example: "Send notifications AND update database at same time",
  },
  {
    value: "INCLUSIVE",
    label: "Inclusive (OR)",
    symbol: "○",
    color: "#f59e0b",
    description: "ONE OR MORE paths based on conditions",
    example:
      "If needs review → review, if needs approval → approve (can do both)",
  },
  {
    value: "EVENT_BASED",
    label: "Event-Based",
    symbol: "⬡",
    color: "#8b5cf6",
    description: "Wait for external event to determine path",
    example: "Wait for customer response OR timeout",
  },
  {
    value: "COMPLEX",
    label: "Complex",
    symbol: "✱",
    color: "#9b59b6",
    description: "Complex gateway with custom evaluation",
    example: "Custom multi-condition routing",
  },
];

const normalizeType = (t) => (t || "").toString().toUpperCase();
const START_EVENT_ID = "__START_EVENT__";
const START_EVENT_OPTION = {
  id: START_EVENT_ID,
  name: "Start Event",
  order: 0,
  isStartEvent: true,
};
const isStartEventPlacement = (value) => String(value) === START_EVENT_ID;
const toGatewayTaskId = (value) =>
  isStartEventPlacement(value)
    ? null
    : value != null && value !== ""
      ? Number(value)
      : null;
const fromGatewayTaskId = (value) =>
  value != null && value !== "" ? String(value) : START_EVENT_ID;

const getTypeInfo = (type) => {
  const n = normalizeType(type);
  return (
    GATEWAY_TYPES.find(
      (t) => normalizeType(t.value || t.label || t.id) === n,
    ) || GATEWAY_TYPES[0]
  );
};
const GATEWAY_PRESETS = [
  {
    id: "approval-rejection",
    name: "Approval / Rejection",
    description: "Standard approval flow with approve and reject paths",
    icon: "✓",
    category: "Decision",
    config: {
      type: "EXCLUSIVE",
      name: "Approval Decision",
      branches: [
        { condition: "approved == true", isApprove: true },
        { endEventName: "Rejected", condition: "approved == false" },
      ],
    },
  },
  {
    id: "approval-revision",
    name: "Approval with Revision",
    description: "Approve, reject, or send back for revision",
    icon: "↻",
    category: "Decision",
    config: {
      type: "EXCLUSIVE",
      name: "Review Decision",
      branches: [
        { condition: "status == 'approved'" },
        { condition: "status == 'revision'", isLoopBack: true },
        { endEventName: "Rejected", condition: "status == 'rejected'" },
      ],
    },
  },
  {
    id: "parallel-review",
    name: "Parallel Review",
    description: "Multiple reviewers work simultaneously, wait for all",
    icon: "⇉",
    category: "Parallel",
    config: {
      type: "PARALLEL",
      name: "Parallel Review",
      branches: [{ label: "Reviewer 1" }, { label: "Reviewer 2" }],
      needsConverge: true,
    },
  },
  {
    id: "parallel-tasks",
    name: "Parallel Tasks",
    description: "Execute multiple tasks at the same time",
    icon: "⋈",
    category: "Parallel",
    config: {
      type: "PARALLEL",
      name: "Split Work",
      branches: [{ label: "Task A" }, { label: "Task B" }],
      needsConverge: true,
    },
  },
  {
    id: "retry-loop",
    name: "Retry Loop",
    description: "Retry failed task up to N times before failing",
    icon: "🔄",
    category: "Loop",
    config: {
      type: "EXCLUSIVE",
      name: "Retry Check",
      branches: [
        { condition: "success == true" },
        { condition: "retryCount < 3", isLoopBack: true },
        { endEventName: "Max Retries Exceeded", condition: "retryCount >= 3" },
      ],
    },
  },
  {
    id: "conditional-steps",
    name: "Conditional Steps",
    description: "Execute optional steps based on conditions",
    icon: "◇",
    category: "Inclusive",
    config: {
      type: "INCLUSIVE",
      name: "Required Steps",
      branches: [
        { condition: "needsStep1 == true" },
        { condition: "needsStep2 == true" },
      ],
      needsConverge: true,
    },
  },
  {
    id: "three-way-split",
    name: "Three-Way Decision",
    description: "Route to one of three different paths",
    icon: "⋔",
    category: "Decision",
    config: {
      type: "EXCLUSIVE",
      name: "Decision Point",
      branches: [
        { condition: "priority == 'high'" },
        { condition: "priority == 'medium'" },
        { condition: "priority == 'low'" },
      ],
    },
  },
  {
    id: "timeout-event",
    name: "Timeout Handler",
    description: "Wait for response or handle timeout",
    icon: "⏱",
    category: "Event",
    config: {
      type: "EVENT_BASED",
      name: "Wait for Response",
      branches: [
        { condition: "response received" },
        { endEventName: "Timeout", condition: "timeout after 24h" },
      ],
    },
  },
];

const WIZARD_STEPS = [
  { id: 1, name: "Type", description: "Choose gateway type" },
  { id: 2, name: "Placement", description: "Select where to place" },
  { id: 3, name: "Branches", description: "Configure paths" },
  { id: 4, name: "Review", description: "Confirm settings" },
];

const createBranchId = () =>
  `br-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

function GatewayConfigModal({
  open,
  onClose,
  onSave,
  tasks = [],
  initialGateways = [],
  editGateway = null, // Single gateway to edit (wizard mode)
  wizardMode = false, // Enable step-by-step wizard
}) {
  const [gateways, setGateways] = React.useState([]);
  const [wizardStep, setWizardStep] = React.useState(1);
  const [, setSelectedPreset] = React.useState(null);
  const [showPresets, setShowPresets] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);

  // Initialize gateways from props
  React.useEffect(() => {
    if (open) {
      if (editGateway) {
        // Editing single gateway in wizard mode
        setGateways([
          {
            ...editGateway,
            id: editGateway.id || createBranchId(),
            afterTaskId: fromGatewayTaskId(
              editGateway.afterTaskId ?? editGateway.after_task_id,
            ),
            convergeGatewayName:
              editGateway.convergeGatewayName ||
              editGateway.converge_gateway_name ||
              "",
            convergeToEnd: !!(
              editGateway.convergeToEnd ?? editGateway.converge_to_end
            ),
            convergeAtGatewayId:
              editGateway.convergeAtGatewayId ??
              editGateway.converge_at_gateway_id ??
              "",
            branches: (editGateway.branches || []).map((b) => ({
              ...b,
              id: b.id || createBranchId(),
            })),
          },
        ]);
        setWizardStep(wizardMode ? 1 : 1);
        setShowPresets(false);
      } else {
        setGateways(
          initialGateways.length > 0
            ? initialGateways.map((g) => ({
                ...g,
                id: g.id || createBranchId(),
                afterTaskId: fromGatewayTaskId(
                  g.afterTaskId ?? g.after_task_id,
                ),
                convergeGatewayName:
                  g.convergeGatewayName || g.converge_gateway_name || "",
                convergeToEnd: !!(g.convergeToEnd ?? g.converge_to_end),
                convergeAtGatewayId:
                  g.convergeAtGatewayId ?? g.converge_at_gateway_id ?? "",
                branches: (g.branches || []).map((b) => ({
                  ...b,
                  id: b.id || createBranchId(),
                })),
              }))
            : [],
        );
        setWizardStep(1);
        setShowPresets(true);
      }
      setSelectedPreset(null);
    }
  }, [open, initialGateways, editGateway, wizardMode]);

  // Get task options for dropdowns — include subprocess rows from
  // GET /bpmn-editor/process/:id/tasks (isSubProcess + childProcessName).
  // Their taskId is submitted as afterTaskId/targetTaskId like a normal task.
  const taskOptions = React.useMemo(() => {
    return (tasks || [])
      .map((t) => {
        const id = t.taskId ?? t.task_id ?? t.id;
        if (id == null || id === "") return null;

        const isSubProcess = !!(
          t.isSubProcess ??
          t.is_sub_process ??
          t.isChildProcess ??
          t.is_child_process
        );
        const childName =
          t.childProcessName ||
          t.child_process_name ||
          t.process_name ||
          t.processName ||
          "";
        const taskName =
          t.taskName || t.task_name || t.name || "";

        let name;
        if (isSubProcess) {
          name = childName
            ? `${childName} (Subprocess)`
            : taskName
              ? `${taskName} (Subprocess)`
              : `Subprocess #${id}`;
        } else {
          name = taskName || `Task ${id}`;
        }

        return {
          id,
          name,
          order: t.order ?? 0,
          isSubProcess,
        };
      })
      .filter(Boolean);
  }, [tasks]);

  const placementOptions = React.useMemo(
    () => [START_EVENT_OPTION, ...taskOptions],
    [taskOptions],
  );

  const getPlacementName = (afterTaskId) => {
    if (isStartEventPlacement(afterTaskId)) return START_EVENT_OPTION.name;
    return (
      taskOptions.find((t) => String(t.id) === String(afterTaskId))?.name ||
      "Not selected"
    );
  };

  // Get tasks that can be targeted from a specific gateway position
  const getTargetTaskOptions = (afterTaskId) => {
    if (!afterTaskId || isStartEventPlacement(afterTaskId)) return taskOptions;
    // For loops/backtracking, allow ALL tasks, not just those after
    return taskOptions;
  };

  // Check if a branch creates a loop (backward flow)
  const isLoopBranch = (afterTaskId, targetTaskId) => {
    if (!afterTaskId || isStartEventPlacement(afterTaskId) || !targetTaskId)
      return false;
    const afterTask = taskOptions.find(
      (t) => String(t.id) === String(afterTaskId),
    );
    const targetTask = taskOptions.find(
      (t) => String(t.id) === String(targetTaskId),
    );
    if (!afterTask || !targetTask) return false;
    // Loop if target task comes before or at same position as gateway
    return targetTask.order <= afterTask.order;
  };

  // Check if target task exists in the task list
  const isValidTargetTask = (targetTaskId) => {
    if (!targetTaskId) return true; // Empty is valid (goes to end event)
    return taskOptions.some((t) => String(t.id) === String(targetTaskId));
  };

  // Validate gateway configuration
  const validateGateway = (gateway) => {
    const errors = [];
    const warnings = [];
    const isParallel = normalizeType(gateway.type) === "PARALLEL";

    // Check afterTaskId exists
    if (!gateway.afterTaskId) {
      errors.push("Gateway must be placed after a task");
    } else if (
      !isStartEventPlacement(gateway.afterTaskId) &&
      !taskOptions.some((t) => String(t.id) === String(gateway.afterTaskId))
    ) {
      errors.push("Selected 'After Task' does not exist");
    }

    // Parallel/Inclusive/etc must join, except Exclusive with End + branch(es).
    if (requiresConvergence(gateway)) {
      if (
        !gateway.convergeAtTaskId &&
        !gateway.convergeToEnd &&
        !gateway.convergeAtGatewayId
      ) {
        errors.push(
          "A join point is required (a task, the End event, or another gateway)",
        );
      }
    }

    // Check branches
    gateway.branches.forEach((branch, idx) => {
      // Parallel gateways must always target a task and cannot end at an event
      if (isParallel) {
        if (!branch.targetTaskId) {
          errors.push(
            `Branch ${idx + 1}: Target task is required for parallel gateways`,
          );
        }
        if (branch.endEventName) {
          errors.push(
            `Branch ${idx + 1}: End events are not allowed for parallel gateways`,
          );
        }
        return;
      }

      // Check if target task exists
      if (branch.targetTaskId && !isValidTargetTask(branch.targetTaskId)) {
        errors.push(`Branch ${idx + 1}: Target task does not exist`);
      }

      // Warn if loop branch has no condition (risk of infinite loop)
      if (
        isLoopBranch(gateway.afterTaskId, branch.targetTaskId) &&
        !branch.condition
      ) {
        warnings.push(
          `Branch ${idx + 1}: Loop branch should have a condition to prevent infinite loops`,
        );
      }
    });

    // Two branches routing to the same task/gateway is conflicting/redundant
    // routing — each outgoing path must go somewhere distinct (a blank
    // target, the shared End event, is exempt since multiple branches may
    // legitimately end there).
    const targetCounts = new Map();
    gateway.branches.forEach((branch) => {
      if (!branch.targetTaskId) return;
      const key = String(branch.targetTaskId);
      targetCounts.set(key, (targetCounts.get(key) || 0) + 1);
    });
    gateway.branches.forEach((branch, idx) => {
      if (
        branch.targetTaskId &&
        targetCounts.get(String(branch.targetTaskId)) > 1
      ) {
        errors.push(
          `Branch ${idx + 1}: Another branch already routes to this same target`,
        );
      }
    });

    // Check if at least one branch has a target or end event
    const hasValidBranch = gateway.branches.some(
      (b) => b.targetTaskId || b.endEventName,
    );
    if (!hasValidBranch) {
      errors.push("At least one branch must have a target task or end event");
    }

    // EXCLUSIVE gateways need at least 2 branches
    if (
      normalizeType(gateway.type) === "EXCLUSIVE" &&
      gateway.branches.length < 2
    ) {
      errors.push("Exclusive gateways require at least 2 branches");
    }

    // PARALLEL gateways should have at least 2 branches
    if (
      normalizeType(gateway.type) === "PARALLEL" &&
      gateway.branches.length < 2
    ) {
      errors.push("Parallel gateways require at least 2 branches");
    }

    return { errors, warnings, isValid: errors.length === 0 };
  };

  const getConvergeTaskOptions = (afterTaskId, branchTargetIds = []) => {
    const selectedBranchTargets = new Set(
      branchTargetIds.map((id) => String(id)),
    );
    return getTargetTaskOptions(afterTaskId).filter(
      (t) => !selectedBranchTargets.has(String(t.id)),
    );
  };

  // Best-guess join point for a gateway that needs one but doesn't have it
  // set yet: the task right after the furthest-along branch target, so every
  // branch (including shorter ones) has somewhere to land. Falls back to End
  // when no branch continues onto the workflow.
  const suggestConvergeTarget = (gateway) => {
    const continuingOrders = (gateway.branches || [])
      .map((b) => b.targetTaskId)
      .filter((t) => t && !(typeof t === "string" && t.startsWith("__GW__")))
      .map(
        (t) => taskOptions.find((o) => String(o.id) === String(t))?.order,
      )
      .filter((o) => Number.isFinite(o));
    if (continuingOrders.length === 0) return "__END__";
    const maxOrder = Math.max(...continuingOrders);
    const next = taskOptions.find((o) => o.order === maxOrder + 1);
    return next ? String(next.id) : "__END__";
  };

  const addGateway = () => {
    const newGateway = {
      id: createBranchId(),
      type: "EXCLUSIVE",
      name: "",
      afterTaskId: START_EVENT_ID,
      convergeAtTaskId: "",
      convergeGatewayName: "",
      convergeToEnd: false,
      convergeAtGatewayId: "",
      branches: [
        {
          id: createBranchId(),
          targetTaskId: "",
          condition: "",
          isDefault: false,
          endEventName: "",
          connectToEnd: true,
        },
        {
          id: createBranchId(),
          targetTaskId: "",
          condition: "",
          isDefault: false,
          endEventName: "",
          connectToEnd: true,
        },
      ],
    };
    setGateways((prev) => [...prev, newGateway]);
  };

  const removeGateway = (gwId) => {
    setGateways((prev) => prev.filter((g) => g.id !== gwId));
  };

  const updateGateway = (gwId, field, value) => {
    setGateways((prev) =>
      prev.map((g) => {
        if (g.id !== gwId) return g;
        const updated = { ...g, [field]: value };
        // Converge-target dropdown sentinels:
        //  "__END__"    → synchronize, then flow into the single End (convergeToEnd)
        //  "__GW__<id>" → join flows into that gateway's split (join → split)
        //  any task id  → converge at that task
        if (field === "convergeAtTaskId") {
          if (value === "__END__") {
            updated.convergeToEnd = true;
            updated.convergeAtTaskId = "";
            updated.convergeAtGatewayId = "";
          } else if (typeof value === "string" && value.startsWith("__GW__")) {
            updated.convergeAtGatewayId = value.slice(6);
            updated.convergeToEnd = false;
            updated.convergeAtTaskId = "";
          } else {
            updated.convergeToEnd = false;
            updated.convergeAtGatewayId = "";
          }
        }
        // Clear convergence settings if switching to EXCLUSIVE or EVENT_BASED
        if (
          field === "type" &&
          (normalizeType(value) === "EXCLUSIVE" ||
            normalizeType(value) === "EVENT_BASED")
        ) {
          updated.convergeAtTaskId = "";
          updated.convergeGatewayName = "";
        }
        // Parallel gateways cannot end at named end events
        if (field === "type" && normalizeType(value) === "PARALLEL") {
          updated.branches = (updated.branches || []).map((b) => ({
            ...b,
            endEventName: "",
            isDefault: false,
          }));
        }
        // Moving the gateway changes which branches loop back, so recompute the
        // isLoopBack flag for every branch against the new placement.
        if (field === "afterTaskId") {
          updated.branches = (updated.branches || []).map((b) => ({
            ...b,
            isLoopBack: isLoopBranch(value, b.targetTaskId),
          }));
        }
        return updated;
      }),
    );
  };

  const addBranch = (gwId) => {
    setGateways((prev) =>
      prev.map((g) => {
        if (g.id !== gwId) return g;
        const updated = {
          ...g,
          branches: [
            ...g.branches,
            {
              id: createBranchId(),
              targetTaskId: "",
              condition: "",
              isDefault: false,
              endEventName: "",
              connectToEnd: true,
            },
          ],
        };
        return exclusiveSkipsConvergence(updated)
          ? { ...updated, ...EMPTY_CONVERGE_FIELDS }
          : updated;
      }),
    );
  };

  const removeBranch = (gwId, branchId) => {
    setGateways((prev) =>
      prev.map((g) => {
        if (g.id !== gwId) return g;
        const minBranches = normalizeType(g.type) === "EXCLUSIVE" ? 2 : 1;
        if (g.branches.length <= minBranches) return g;
        const updated = {
          ...g,
          branches: g.branches.filter((b) => b.id !== branchId),
        };
        return exclusiveSkipsConvergence(updated)
          ? { ...updated, ...EMPTY_CONVERGE_FIELDS }
          : updated;
      }),
    );
  };

  const updateBranch = (gwId, branchId, field, value) => {
    setGateways((prev) =>
      prev.map((g) => {
        if (g.id !== gwId) return g;
        const updated = {
          ...g,
          branches: g.branches.map((b) => {
            if (b.id !== branchId) return b;
            const next = { ...b, [field]: value };
            // Changing the target may turn this branch into (or out of) a
            // loop-back; keep isLoopBack in sync so the saved/previewed payload
            // sets connectToEnd correctly.
            if (field === "targetTaskId") {
              next.isLoopBack = isLoopBranch(g.afterTaskId, value);
            }
            return next;
          }),
        };
        return exclusiveSkipsConvergence(updated)
          ? { ...updated, ...EMPTY_CONVERGE_FIELDS }
          : updated;
      }),
    );
  };

  const handleSave = async () => {
    if (isSaving) return;
    // Validate all gateways before saving
    let hasErrors = false;
    let hasWarnings = false;
    const allErrors = [];
    const allWarnings = [];

    gateways.forEach((g, idx) => {
      const validation = validateGateway(g);
      if (!validation.isValid) {
        hasErrors = true;
        validation.errors.forEach((e) =>
          allErrors.push(`Gateway ${idx + 1}: ${e}`),
        );
      }
      if (validation.warnings.length > 0) {
        hasWarnings = true;
        validation.warnings.forEach((w) =>
          allWarnings.push(`Gateway ${idx + 1}: ${w}`),
        );
      }
    });

    // Block save if there are errors
    if (hasErrors) {
      alert("Cannot save gateways:\n\n" + allErrors.join("\n"));
      return;
    }

    // Warn user about potential issues but allow save
    if (hasWarnings) {
      const proceed = window.confirm(
        "Warning - Loop branches without conditions detected:\n\n" +
          allWarnings.join("\n") +
          "\n\nLoop branches should have conditions to prevent infinite loops in execution.\n\nDo you want to continue anyway?",
      );
      if (!proceed) return;
    }

    // Transform gateways to API format
    const apiGateways = gateways.map((g) => {
      const isParallel = normalizeType(g.type) === "PARALLEL";
      const gateway = {
        id: g.id,
        type: String(g.type || "EXCLUSIVE").toUpperCase(),
        name: g.name || undefined,
        afterTaskId: toGatewayTaskId(g.afterTaskId),
        // Transform branches according to API format
        // Note: Do NOT filter out branches with no targetTaskId AND no endEventName
        // These represent branches to the default end event
        branches: g.branches.map((b) => {
          if (isParallel) {
            return {
              targetTaskId: b.targetTaskId ? Number(b.targetTaskId) : null,
            };
          }
          // Case 1: Branch goes to a named end event
          if (b.endEventName && !b.targetTaskId) {
            const branch = {
              endEventName: b.endEventName,
            };
            if (b.condition && !b.isDefault) branch.condition = b.condition;
            if (b.isDefault) branch.isDefault = true;
            return branch;
          }

          // Case 2: Branch goes to a specific task
          if (b.targetTaskId) {
            const branch = {
              targetTaskId: Number(b.targetTaskId),
            };
            // Recompute loop status from live task order rather than trusting a
            // possibly-stale flag (e.g. on branches loaded but never edited), so
            // a loop-back branch never tells the backend to connect it to End.
            const loop = isLoopBranch(g.afterTaskId, b.targetTaskId);
            if (!loop) branch.connectToEnd = b.connectToEnd !== false;
            if (b.condition && !b.isDefault) branch.condition = b.condition;
            if (b.isDefault) branch.isDefault = true;
            return branch;
          }

          // Case 3: Branch goes to default end event (no targetTaskId, no endEventName)
          const branch = {
            targetTaskId: null,
          };
          if (b.condition && !b.isDefault) branch.condition = b.condition;
          if (b.isDefault) branch.isDefault = true;
          return branch;
        }),
      };

      // join → split: resolve the target gateway's local id to its array index.
      const skipConverge = exclusiveSkipsConvergence(g);
      const gwTargetIdx =
        !skipConverge && needsConvergence(g.type) && g.convergeAtGatewayId
          ? gateways.findIndex(
              (x) => String(x.id) === String(g.convergeAtGatewayId),
            )
          : -1;
      gateway.convergeAtGatewayIndex = gwTargetIdx >= 0 ? gwTargetIdx : undefined;
      const hasGwTarget = gateway.convergeAtGatewayIndex != null;
      // The BPMN editor saves gateways individually (each already has a pk id),
      // so also pass the target's pk directly for that path.
      if (hasGwTarget) {
        const targetPk = Number(gateways[gwTargetIdx]?.id);
        if (Number.isFinite(targetPk) && targetPk > 0)
          gateway.convergeAtGatewayId = targetPk;
      }
      gateway.convergeToEnd =
        !skipConverge &&
        !hasGwTarget &&
        needsConvergence(g.type) &&
        !!g.convergeToEnd;
      const convergeNum = Number(g.convergeAtTaskId);
      gateway.convergeAtTaskId =
        !skipConverge &&
        !hasGwTarget &&
        needsConvergence(g.type) &&
        !gateway.convergeToEnd &&
        g.convergeAtTaskId &&
        Number.isFinite(convergeNum) &&
        convergeNum > 0
          ? convergeNum
          : null;
      gateway.convergeGatewayName =
        !skipConverge &&
        needsConvergence(g.type) &&
        String(g.convergeGatewayName || "").trim()
          ? String(g.convergeGatewayName).trim()
          : null;

      return gateway;
    });

    try {
      setIsSaving(true);
      await onSave(apiGateways);
      onClose();
    } catch (error) {
      alert(error?.message || "Failed to save gateway configuration");
    } finally {
      setIsSaving(false);
    }
  };

  const needsConvergence = (type) => {
    const n = normalizeType(type);
    return (
      n === "PARALLEL" || n === "INCLUSIVE" || n === "EXCLUSIVE" || n === "COMPLEX"
    );
  };

  // Converge is required for merging types, except Exclusive with End + branch(es).
  const requiresConvergence = (gatewayOrType) => {
    if (gatewayOrType && typeof gatewayOrType === "object") {
      return gatewayRequiresConvergence(gatewayOrType, needsConvergence);
    }
    return needsConvergence(gatewayOrType);
  };

  const showsConvergence = (gateway) =>
    needsConvergence(gateway?.type) && !exclusiveSkipsConvergence(gateway);

  const needsConditions = (type) => {
    const n = normalizeType(type);
    return n === "EXCLUSIVE" || n === "INCLUSIVE" || n === "EVENT_BASED";
  };

  // Apply a preset to create a new gateway
  const applyPreset = (preset) => {
    setSelectedPreset(preset);
    const newGateway = {
      id: createBranchId(),
      type: preset.config.type,
      name: preset.config.name,
      afterTaskId: START_EVENT_ID,
      convergeAtTaskId: "",
      convergeGatewayName: "",
      convergeToEnd: false,
      convergeAtGatewayId: "",
      branches: preset.config.branches.map((b, idx) => ({
        id: createBranchId(),
        targetTaskId: "",
        condition: b.condition || "",
        endEventName: b.endEventName || "",
        isDefault: false,
        isLoopBack: b.isLoopBack || false,
        connectToEnd: b.isLoopBack ? false : true,
      })),
    };
    setGateways([newGateway]);
    setShowPresets(false);
  };

  // Get current gateway being edited (for wizard mode)
  const currentGateway = gateways[0];

  // Validation for wizard steps
  const canProceed = (step) => {
    if (!currentGateway) return false;
    switch (step) {
      case 1:
        return !!currentGateway.type;
      case 2: {
        const hasAfter =
          !!currentGateway.afterTaskId &&
          (isStartEventPlacement(currentGateway.afterTaskId) ||
            taskOptions.some(
              (t) => String(t.id) === String(currentGateway.afterTaskId),
            ));
        if (!hasAfter) return false;
        if (requiresConvergence(currentGateway)) {
          return (
            !!currentGateway.convergeAtTaskId ||
            !!currentGateway.convergeToEnd ||
            !!currentGateway.convergeAtGatewayId
          );
        }
        return true;
      }
      case 3: {
        const validation = validateGateway(currentGateway);
        return validation.isValid;
      }
      default:
        return true;
    }
  };

  if (!open) return null;

  // Render wizard step content
  const renderWizardStep = () => {
    if (!currentGateway) return null;
    const typeInfo = getTypeInfo(currentGateway.type);

    switch (wizardStep) {
      case 1: // Type Selection
        return (
          <div className="wizard-step-content">
            <h4
              style={{ margin: "0 0 16px 0", color: "#0f172a", fontSize: 16 }}
            >
              Select Gateway Type
            </h4>
            <div
              className="stack-on-phone"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 12,
              }}
            >
              {GATEWAY_TYPES.map((gt) => (
                <div
                  key={gt.value}
                  onClick={() =>
                    updateGateway(currentGateway.id, "type", gt.value)
                  }
                  style={{
                    padding: 16,
                    border: `3px solid ${normalizeType(currentGateway.type) === normalizeType(gt.value) ? gt.color : "#e2e8f0"}`,
                    borderRadius: 12,
                    cursor: "pointer",
                    background:
                      normalizeType(currentGateway.type) ===
                      normalizeType(gt.value)
                        ? `${gt.color}10`
                        : "#fff",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: gt.color,
                        borderRadius: 8,
                        fontWeight: 700,
                        color: "white",
                        fontSize: 18,
                      }}
                    >
                      {gt.symbol}
                    </span>
                    <div>
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#0f172a",
                          fontSize: 14,
                        }}
                      >
                        {gt.label}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{ fontSize: 13, color: "#475569", marginBottom: 8 }}
                  >
                    {gt.description}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#94a3b8",
                      fontStyle: "italic",
                    }}
                  >
                    💡 {gt.example}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 2: // Placement
        return (
          <div className="wizard-step-content">
            <h4
              style={{ margin: "0 0 16px 0", color: "#0f172a", fontSize: 16 }}
            >
              Where should this gateway be placed?
            </h4>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 20,
                padding: 12,
                background: "#f8fafc",
                borderRadius: 8,
              }}
            >
              <span
                style={{
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: typeInfo.color,
                  borderRadius: 8,
                  fontWeight: 700,
                  color: "white",
                  fontSize: 16,
                }}
              >
                {typeInfo.symbol}
              </span>
              <div>
                <div style={{ fontWeight: 600, color: "#0f172a" }}>
                  {typeInfo.label}
                </div>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {typeInfo.description}
                </div>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label
                style={{ fontWeight: 600, marginBottom: 8, display: "block" }}
              >
                Gateway Name (optional)
              </label>
              <input
                value={currentGateway.name}
                onChange={(e) =>
                  updateGateway(currentGateway.id, "name", e.target.value)
                }
                placeholder="e.g., Is Approved?, Check Status"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  border: "2px solid #e5e7eb",
                  borderRadius: 10,
                  fontSize: 14,
                }}
              />
              <small
                style={{ color: "#64748b", marginTop: 4, display: "block" }}
              >
                A descriptive name helps identify this gateway in the diagram
              </small>
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label
                style={{ fontWeight: 600, marginBottom: 8, display: "block" }}
              >
                Place Gateway After
              </label>
              <select
                value={currentGateway.afterTaskId}
                onChange={(e) =>
                  updateGateway(
                    currentGateway.id,
                    "afterTaskId",
                    e.target.value,
                  )
                }
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  border: "2px solid #e5e7eb",
                  borderRadius: 10,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                <option value="">-- Select placement --</option>
                {placementOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.isStartEvent ? t.name : `${t.order}. ${t.name}`}
                  </option>
                ))}
              </select>
            </div>

            {exclusiveSkipsConvergence(currentGateway) && (
              <div
                style={{
                  marginBottom: 16,
                  padding: "10px 12px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 8,
                  color: "#166534",
                  fontSize: 13,
                }}
              >
                No join needed: this Exclusive has continuing branch(es) and a
                separate End path, so converge is cleared automatically.
              </div>
            )}

            {showsConvergence(currentGateway) && (
              <div className="form-group">
                <label
                  style={{
                    fontWeight: 600,
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <span>
                    Converge At Task (Join Point)
                    <span
                      style={{
                        marginLeft: 8,
                        padding: "2px 8px",
                        background: "#fee2e2",
                        color: "#dc2626",
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 500,
                      }}
                    >
                      Required
                    </span>
                  </span>
                  {!currentGateway.convergeToEnd &&
                    !currentGateway.convergeAtGatewayId &&
                    !currentGateway.convergeAtTaskId && (
                      <button
                        type="button"
                        className="secondary-btn sm"
                        title="Pick the task right after where these branches naturally come back together (or End, if none continue)"
                        onClick={() =>
                          updateGateway(
                            currentGateway.id,
                            "convergeAtTaskId",
                            suggestConvergeTarget(currentGateway),
                          )
                        }
                      >
                        Suggest join point
                      </button>
                    )}
                </label>
                <select
                  value={
                    currentGateway.convergeToEnd
                      ? "__END__"
                      : currentGateway.convergeAtGatewayId
                        ? `__GW__${currentGateway.convergeAtGatewayId}`
                        : currentGateway.convergeAtTaskId || ""
                  }
                  onChange={(e) =>
                    updateGateway(
                      currentGateway.id,
                      "convergeAtTaskId",
                      e.target.value,
                    )
                  }
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    border: "2px solid #e5e7eb",
                    borderRadius: 10,
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                >
                  <option value="">-- Select convergence point --</option>
                  {getConvergeTaskOptions(
                    currentGateway.afterTaskId,
                    (currentGateway.branches || [])
                      .map((b) => b.targetTaskId)
                      .filter(Boolean),
                  ).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.order}. {t.name}
                    </option>
                  ))}
                  <option value="__END__">
                    ⏹ End Event — synchronize, then end
                  </option>
                  {gateways
                    .filter((g) => g.id !== currentGateway.id)
                    .map((g) => (
                      <option key={g.id} value={`__GW__${g.id}`}>
                        ▷ Gateway: {g.name || g.type} — join into its split
                      </option>
                    ))}
                </select>
                <small
                  style={{ color: "#64748b", marginTop: 4, display: "block" }}
                >
                  {currentGateway.convergeToEnd
                    ? "Branches synchronize at a join of the same type, then flow into the single End event."
                    : currentGateway.convergeAtGatewayId
                      ? "Branches synchronize at a join that flows directly into the chosen gateway’s split (join → split)."
                      : "Where all paths merge back together. Choose “End Event” if the branches only finish the process, or a gateway to open a new split right after the join."}
                </small>

                <label
                  style={{
                    fontWeight: 600,
                    marginTop: 12,
                    marginBottom: 8,
                    display: "block",
                  }}
                >
                  Join Gateway Label (optional)
                  <span
                    style={{
                      marginLeft: 8,
                      padding: "2px 8px",
                      background: "#dbeafe",
                      color: "#1d4ed8",
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 500,
                    }}
                  >
                    Optional
                  </span>
                </label>
                <input
                  value={currentGateway.convergeGatewayName || ""}
                  onChange={(e) =>
                    updateGateway(
                      currentGateway.id,
                      "convergeGatewayName",
                      e.target.value,
                    )
                  }
                  placeholder="e.g., All checks done"
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    border: "2px solid #e5e7eb",
                    borderRadius: 10,
                    fontSize: 14,
                  }}
                />
                <small
                  style={{ color: "#64748b", marginTop: 4, display: "block" }}
                >
                  Label for the converging (join) gateway
                </small>
              </div>
            )}
          </div>
        );

      case 3: // Branches
        const targetOptions = getTargetTaskOptions(currentGateway.afterTaskId);
        const showConditions = needsConditions(currentGateway.type);
        const allowEndEvent = currentGateway.type !== "PARALLEL";

        return (
          <div className="wizard-step-content">
            <h4 style={{ margin: "0 0 8px 0", color: "#0f172a", fontSize: 16 }}>
              Configure Branches
            </h4>
            <p style={{ margin: "0 0 16px 0", color: "#64748b", fontSize: 13 }}>
              {normalizeType(currentGateway.type) === "EXCLUSIVE" &&
                "Define the possible paths. Only ONE will execute based on conditions."}
              {normalizeType(currentGateway.type) === "PARALLEL" &&
                "Define the tasks that will execute simultaneously."}
              {normalizeType(currentGateway.type) === "INCLUSIVE" &&
                "Define optional paths. One or more can execute based on conditions."}
              {normalizeType(currentGateway.type) === "EVENT_BASED" &&
                "Define the events to wait for."}
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {currentGateway.branches.map((br, brIdx) => {
                const isLoop = isLoopBranch(
                  currentGateway.afterTaskId,
                  br.targetTaskId,
                );
                const isSelfLoop =
                  String(br.targetTaskId) ===
                  String(currentGateway.afterTaskId);
                const invalidTarget =
                  br.targetTaskId && !isValidTargetTask(br.targetTaskId);
                const usedTargetIds = new Set(
                  currentGateway.branches
                    .filter((other) => other.id !== br.id && other.targetTaskId)
                    .map((other) => String(other.targetTaskId)),
                );
                const duplicateTarget =
                  br.targetTaskId && usedTargetIds.has(String(br.targetTaskId));

                return (
                  <div
                    key={br.id}
                    style={{
                      padding: 16,
                      background: isLoop ? "#fef3c7" : "#f8fafc",
                      borderRadius: 10,
                      border: `1px solid ${invalidTarget || duplicateTarget ? "#ef4444" : isLoop ? "#f59e0b" : "#e2e8f0"}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 12,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          color: "#334155",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: typeInfo.color,
                            color: "white",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {brIdx + 1}
                        </span>
                        Branch {brIdx + 1}
                        {isLoop && (
                          <span
                            style={{
                              padding: "2px 8px",
                              background: "#f59e0b",
                              color: "white",
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            🔄 {isSelfLoop ? "SELF-LOOP" : "LOOP BACK"}
                          </span>
                        )}
                      </span>
                      {currentGateway.branches.length > 2 && (
                        <button
                          className="danger-btn sm"
                          onClick={() => removeBranch(currentGateway.id, br.id)}
                          style={{ padding: "4px 12px" }}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {/* Loop Info Banner */}
                    {isLoop && (
                      <div
                        style={{
                          padding: "8px 12px",
                          background: "#fffbeb",
                          border: "1px solid #fde68a",
                          borderRadius: 6,
                          marginBottom: 12,
                          fontSize: 12,
                          color: "#92400e",
                        }}
                      >
                        <strong>🔁 Loop Branch:</strong> This branch will route
                        back to{" "}
                        {isSelfLoop
                          ? "the same task (retry pattern)"
                          : "an earlier task in the flow"}
                        .
                        {!br.condition && (
                          <span
                            style={{
                              display: "block",
                              marginTop: 4,
                              color: "#dc2626",
                            }}
                          >
                            ⚠️ Add a condition to prevent infinite loops!
                          </span>
                        )}
                      </div>
                    )}

                    {/* Invalid Target Warning */}
                    {invalidTarget && (
                      <div
                        style={{
                          padding: "8px 12px",
                          background: "#fef2f2",
                          border: "1px solid #fecaca",
                          borderRadius: 6,
                          marginBottom: 12,
                          fontSize: 12,
                          color: "#dc2626",
                        }}
                      >
                        <strong>❌ Invalid Target:</strong> The selected target
                        task does not exist. Please select a valid task.
                      </div>
                    )}

                    {/* Duplicate Target Warning */}
                    {duplicateTarget && (
                      <div
                        style={{
                          padding: "8px 12px",
                          background: "#fef2f2",
                          border: "1px solid #fecaca",
                          borderRadius: 6,
                          marginBottom: 12,
                          fontSize: 12,
                          color: "#dc2626",
                        }}
                      >
                        <strong>⚠️ Duplicate Target:</strong> Another branch on
                        this gateway already routes to this task. Choose a
                        different target for each branch.
                      </div>
                    )}

                    <div
                      className="stack-on-phone"
                      style={{
                        display: "grid",
                        gridTemplateColumns: allowEndEvent ? "1fr 1fr" : "1fr",
                        gap: 12,
                      }}
                    >
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            marginBottom: 4,
                            display: "block",
                          }}
                        >
                          Target Task
                        </label>
                        <select
                          value={br.targetTaskId || ""}
                          onChange={(e) =>
                            updateBranch(
                              currentGateway.id,
                              br.id,
                              "targetTaskId",
                              e.target.value,
                            )
                          }
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: `1px solid ${invalidTarget || duplicateTarget ? "#ef4444" : "#d1d5db"}`,
                            borderRadius: 8,
                            fontSize: 13,
                            background:
                              invalidTarget || duplicateTarget ? "#fef2f2" : "#fff",
                          }}
                        >
                          <option value="">
                            {allowEndEvent
                              ? "-- Select task or use End Event --"
                              : "-- Select task --"}
                          </option>
                          {/* Show task order indicator for loop detection */}
                          {targetOptions.map((t) => {
                            const gatewayTask = taskOptions.find(
                              (gt) =>
                                String(gt.id) ===
                                String(currentGateway.afterTaskId),
                            );
                            const isLoopTarget =
                              gatewayTask && t.order <= gatewayTask.order;
                            const isUsedElsewhere = usedTargetIds.has(String(t.id));
                            return (
                              <option
                                key={t.id}
                                value={t.id}
                                disabled={isUsedElsewhere}
                              >
                                {t.order}. {t.name}{" "}
                                {isLoopTarget ? " ← (creates loop)" : ""}
                                {isUsedElsewhere ? " (already used)" : ""}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      {allowEndEvent && (
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              marginBottom: 4,
                              display: "block",
                            }}
                          >
                            End Event Name
                            <span style={{ fontWeight: 400, color: "#94a3b8" }}>
                              {" "}
                              (if no target)
                            </span>
                          </label>
                          <input
                            value={br.endEventName || ""}
                            onChange={(e) =>
                              updateBranch(
                                currentGateway.id,
                                br.id,
                                "endEventName",
                                e.target.value,
                              )
                            }
                            placeholder="e.g., Rejected, Cancelled"
                            disabled={!!br.targetTaskId}
                            style={{
                              width: "100%",
                              padding: "10px 12px",
                              border: "1px solid #d1d5db",
                              borderRadius: 8,
                              fontSize: 13,
                              background: br.targetTaskId ? "#f3f4f6" : "#fff",
                              color: br.targetTaskId ? "#9ca3af" : "#111827",
                            }}
                          />
                        </div>
                      )}

                      {showConditions && (
                        <div
                          className="form-group"
                          style={{ marginBottom: 0, gridColumn: "1 / -1" }}
                        >
                          <label
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              marginBottom: 4,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            Condition
                            <span style={{ fontWeight: 400, color: "#94a3b8" }}>
                              (when should this path execute?)
                            </span>
                            {isLoop && !br.condition && (
                              <span
                                style={{
                                  color: "#dc2626",
                                  fontSize: 11,
                                  fontWeight: 600,
                                }}
                              >
                                ⚠️ Required for loops
                              </span>
                            )}
                          </label>
                          <input
                            value={br.condition || ""}
                            onChange={(e) =>
                              updateBranch(
                                currentGateway.id,
                                br.id,
                                "condition",
                                e.target.value,
                              )
                            }
                            placeholder={
                              isLoop
                                ? "e.g., retryCount < 3, status == 'revision'"
                                : "e.g., approved == true, status == 'pending'"
                            }
                            style={{
                              width: "100%",
                              padding: "10px 12px",
                              border: `1px solid ${isLoop && !br.condition ? "#f59e0b" : "#d1d5db"}`,
                              borderRadius: 8,
                              fontSize: 13,
                              background:
                                isLoop && !br.condition ? "#fffbeb" : "#fff",
                            }}
                          />
                        </div>
                      )}

                      {/* Connect to End Event toggle */}
                      {br.targetTaskId &&
                        !isLoop &&
                        !br.endEventName &&
                        normalizeType(currentGateway.type) !== "PARALLEL" && (
                          <div style={{ gridColumn: "1 / -1", marginTop: 4 }}>
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                cursor: "pointer",
                                userSelect: "none",
                                fontSize: 13,
                                fontWeight: 500,
                                color:
                                  br.connectToEnd !== false
                                    ? "#059669"
                                    : "#6b7280",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={br.connectToEnd !== false}
                                onChange={(e) =>
                                  updateBranch(
                                    currentGateway.id,
                                    br.id,
                                    "connectToEnd",
                                    e.target.checked,
                                  )
                                }
                                style={{
                                  width: 15,
                                  height: 15,
                                  cursor: "pointer",
                                  accentColor: "#059669",
                                }}
                              />
                              Connect this branch's chain to End Event
                              <span
                                style={{
                                  marginLeft: 4,
                                  fontSize: 11,
                                  color: "#94a3b8",
                                  fontWeight: 400,
                                }}
                              >
                                (last task in chain → End)
                              </span>
                            </label>
                          </div>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              className="secondary-btn"
              onClick={() => addBranch(currentGateway.id)}
              style={{ marginTop: 12 }}
            >
              + Add Another Branch
            </button>
          </div>
        );

      case 4: // Review
        return (
          <div className="wizard-step-content">
            <h4
              style={{ margin: "0 0 16px 0", color: "#0f172a", fontSize: 16 }}
            >
              Review Gateway Configuration
            </h4>

            <div
              style={{
                background: "#f8fafc",
                borderRadius: 12,
                padding: 16,
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 16,
                  paddingBottom: 16,
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <span
                  style={{
                    width: 48,
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: typeInfo.color,
                    borderRadius: 10,
                    fontWeight: 700,
                    color: "white",
                    fontSize: 22,
                  }}
                >
                  {typeInfo.symbol}
                </span>
                <div>
                  <div
                    style={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}
                  >
                    {currentGateway.name || typeInfo.label}
                  </div>
                  <div style={{ fontSize: 13, color: "#64748b" }}>
                    {typeInfo.description}
                  </div>
                </div>
              </div>

              <div
                className="stack-on-phone"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  marginBottom: 16,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: 4,
                    }}
                  >
                    After Task/Gateway/Event
                  </div>
                  <div style={{ fontWeight: 600, color: "#1e293b" }}>
                    {getPlacementName(currentGateway.afterTaskId)}
                  </div>
                </div>
                {showsConvergence(currentGateway) && (
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: 4,
                      }}
                    >
                      Converge At
                    </div>
                    <div style={{ fontWeight: 600, color: "#1e293b" }}>
                      {currentGateway.convergeToEnd
                        ? "⏹ End Event (synchronize, then end)"
                        : taskOptions.find(
                            (t) =>
                              String(t.id) ===
                              String(currentGateway.convergeAtTaskId),
                          )?.name || "Not selected"}
                    </div>
                  </div>
                )}
                {showsConvergence(currentGateway) && (
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: 4,
                      }}
                    >
                      Join Gateway Label
                    </div>
                    <div style={{ fontWeight: 600, color: "#1e293b" }}>
                      {currentGateway.convergeGatewayName || "Not provided"}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: 8,
                  }}
                >
                  Branches ({currentGateway.branches.length})
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {currentGateway.branches.map((br, idx) => {
                    const targetTask = taskOptions.find(
                      (t) => String(t.id) === String(br.targetTaskId),
                    );
                    const isLoop = isLoopBranch(
                      currentGateway.afterTaskId,
                      br.targetTaskId,
                    );
                    const isSelfLoop =
                      String(br.targetTaskId) ===
                      String(currentGateway.afterTaskId);
                    const needsConditionWarning = isLoop && !br.condition;

                    return (
                      <div
                        key={br.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 12px",
                          background: isLoop ? "#fffbeb" : "#fff",
                          borderRadius: 8,
                          border: `1px solid ${needsConditionWarning ? "#f59e0b" : "#e2e8f0"}`,
                        }}
                      >
                        <span
                          style={{
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: isLoop ? "#f59e0b" : "#e2e8f0",
                            color: isLoop ? "white" : "#475569",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {idx + 1}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: 500,
                              color: "#1e293b",
                              fontSize: 13,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            {isLoop ? "🔄" : "→"}{" "}
                            {targetTask
                              ? targetTask.name
                              : br.endEventName
                                ? `End: ${br.endEventName}`
                                : "No target"}
                            {isLoop && (
                              <span
                                style={{
                                  padding: "2px 6px",
                                  background: "#f59e0b",
                                  color: "white",
                                  borderRadius: 4,
                                  fontSize: 9,
                                  fontWeight: 600,
                                }}
                              >
                                {isSelfLoop ? "SELF-LOOP" : "LOOP"}
                              </span>
                            )}
                          </div>
                          {br.condition && (
                            <div style={{ fontSize: 11, color: "#64748b" }}>
                              When: {br.condition}
                            </div>
                          )}
                          {needsConditionWarning && (
                            <div style={{ fontSize: 11, color: "#dc2626" }}>
                              ⚠️ Loop should have a condition
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Validation Warnings Summary */}
                {(() => {
                  const validation = validateGateway(currentGateway);
                  if (validation.warnings.length === 0) return null;
                  return (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        background: "#fffbeb",
                        border: "1px solid #fde68a",
                        borderRadius: 8,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#92400e",
                          fontSize: 12,
                          marginBottom: 4,
                        }}
                      >
                        ⚠️ Warnings
                      </div>
                      {validation.warnings.map((w, i) => (
                        <div key={i} style={{ fontSize: 11, color: "#92400e" }}>
                          • {w}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div
        className="modal-panel"
        style={{ maxWidth: wizardMode || editGateway ? 700 : 1000 }}
      >
        <div className="modal-header">
          <h3 className="modal-title">
            {editGateway
              ? "Edit Gateway"
              : wizardMode
                ? "Add Gateway"
                : "Configure BPMN Gateways"}
          </h3>
          {(wizardMode || editGateway) && gateways.length > 0 && (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {WIZARD_STEPS.map((step) => (
                <div
                  key={step.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    borderRadius: 20,
                    background:
                      wizardStep === step.id
                        ? "#3b82f6"
                        : wizardStep > step.id
                          ? "#22c55e"
                          : "#e2e8f0",
                    color: wizardStep >= step.id ? "#fff" : "#64748b",
                    fontSize: 12,
                    fontWeight: 600,
                    transition: "all 0.2s ease",
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "50%",
                      fontSize: 10,
                    }}
                  >
                    {wizardStep > step.id ? "✓" : step.id}
                  </span>
                  {step.name}
                </div>
              ))}
            </div>
          )}
        </div>
        <div
          className="modal-body"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {/* Preset Selection (for new gateways) */}
          {!editGateway && showPresets && gateways.length === 0 && (
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <div>
                  <h4 style={{ margin: 0, color: "#0f172a", fontSize: 16 }}>
                    Quick Start with a Pattern
                  </h4>
                  <p
                    style={{
                      margin: "4px 0 0 0",
                      color: "#64748b",
                      fontSize: 13,
                    }}
                  >
                    Choose a common gateway pattern or start from scratch
                  </p>
                </div>
                <button
                  className="secondary-btn sm"
                  onClick={() => {
                    addGateway();
                    setShowPresets(false);
                  }}
                >
                  Start from Scratch
                </button>
              </div>

              {/* Preset Categories */}
              {["Decision", "Parallel", "Inclusive", "Loop", "Event"].map(
                (category) => {
                  const categoryPresets = GATEWAY_PRESETS.filter(
                    (p) => p.category === category,
                  );
                  if (categoryPresets.length === 0) return null;
                  return (
                    <div key={category} style={{ marginBottom: 16 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: 8,
                        }}
                      >
                        {category} Patterns
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(min(200px, 100%), 1fr))",
                          gap: 10,
                        }}
                      >
                        {categoryPresets.map((preset) => {
                          const gt = getTypeInfo(preset.config.type);
                          return (
                            <div
                              key={preset.id}
                              onClick={() => applyPreset(preset)}
                              style={{
                                padding: 14,
                                border: "2px solid #e2e8f0",
                                borderRadius: 10,
                                cursor: "pointer",
                                background: "#fff",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor =
                                  gt?.color || "#3b82f6";
                                e.currentTarget.style.transform =
                                  "translateY(-2px)";
                                e.currentTarget.style.boxShadow =
                                  "0 4px 12px rgba(0,0,0,0.08)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = "#e2e8f0";
                                e.currentTarget.style.transform =
                                  "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  marginBottom: 8,
                                }}
                              >
                                <span style={{ fontSize: 20 }}>
                                  {preset.icon}
                                </span>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color: "#0f172a",
                                    fontSize: 14,
                                  }}
                                >
                                  {preset.name}
                                </span>
                              </div>
                              <div style={{ fontSize: 12, color: "#64748b" }}>
                                {preset.description}
                              </div>
                              <div
                                style={{
                                  marginTop: 8,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                <span
                                  style={{
                                    width: 18,
                                    height: 18,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: gt?.color || "#6b7280",
                                    borderRadius: 4,
                                    color: "white",
                                    fontSize: 10,
                                    fontWeight: 700,
                                  }}
                                >
                                  {gt?.symbol}
                                </span>
                                <span
                                  style={{ fontSize: 11, color: "#94a3b8" }}
                                >
                                  {gt?.label}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          )}

          {/* Wizard Mode Content */}
          {(wizardMode || editGateway) &&
            gateways.length > 0 &&
            renderWizardStep()}

          {/* Standard Mode (multiple gateways) */}
          {!wizardMode && !editGateway && !showPresets && (
            <>
              {/* Gateway Type Legend */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))",
                  gap: 12,
                  marginBottom: 20,
                  padding: 16,
                  background: "#f8fafc",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              >
                {GATEWAY_TYPES.map((gt) => (
                  <div
                    key={gt.value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#e0f2fe",
                        borderRadius: 6,
                        fontWeight: 700,
                        color: "#0284c7",
                        fontSize: 14,
                      }}
                    >
                      {gt.symbol}
                    </span>
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 13,
                          color: "#0f172a",
                        }}
                      >
                        {gt.label}
                      </div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>
                        {gt.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Gateway Button */}
              <button
                className="primary-btn"
                onClick={addGateway}
                style={{ marginBottom: 16 }}
                disabled={taskOptions.length === 0}
              >
                + Add Gateway
              </button>

              {taskOptions.length === 0 && (
                <div
                  style={{
                    padding: 16,
                    background: "#fef3c7",
                    border: "1px solid #f59e0b",
                    borderRadius: 8,
                    color: "#92400e",
                    marginBottom: 16,
                  }}
                >
                  No tasks available. This process needs tasks to configure
                  gateways.
                </div>
              )}

              {/* Gateways List */}
              {gateways.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: 32,
                    color: "#6b7280",
                    background: "#f9fafb",
                    borderRadius: 8,
                    border: "1px dashed #d1d5db",
                  }}
                >
                  No gateways configured. Click "Add Gateway" to create one.
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  {gateways.map((gw, gwIdx) => {
                    const typeInfo = getTypeInfo(gw.type);
                    const targetOptions = getTargetTaskOptions(gw.afterTaskId);
                    const branchTargetIds = gw.branches
                      .map((b) => b.targetTaskId)
                      .filter(Boolean);
                    const convergeOptions = getConvergeTaskOptions(
                      gw.afterTaskId,
                      branchTargetIds,
                    );
                    const showConverge = showsConvergence(gw);
                    const showConditions = needsConditions(gw.type);
                    const allowEndEvent = normalizeType(gw.type) !== "PARALLEL";

                    return (
                      <div
                        key={gw.id}
                        style={{
                          border: "2px solid #e2e8f0",
                          borderRadius: 12,
                          padding: 16,
                          background: "#fff",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        }}
                      >
                        {/* Gateway Header */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 16,
                            paddingBottom: 12,
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <span
                              style={{
                                width: 36,
                                height: 36,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                  "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                borderRadius: 8,
                                fontWeight: 700,
                                color: "white",
                                fontSize: 16,
                              }}
                            >
                              {gwIdx + 1}
                            </span>
                            <div>
                              <span
                                style={{
                                  fontWeight: 700,
                                  color: "#0f172a",
                                  fontSize: 15,
                                }}
                              >
                                Gateway
                              </span>
                              <span
                                style={{
                                  marginLeft: 8,
                                  padding: "2px 8px",
                                  background: "#e0f2fe",
                                  color: "#0284c7",
                                  borderRadius: 4,
                                  fontSize: 12,
                                  fontWeight: 600,
                                }}
                              >
                                {typeInfo.symbol} {typeInfo.label}
                              </span>
                            </div>
                          </div>
                          <button
                            className="danger-btn sm"
                            onClick={() => removeGateway(gw.id)}
                          >
                            Remove Gateway
                          </button>
                        </div>

                        {/* Gateway Configuration */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: showConverge
                              ? "repeat(5, 1fr)"
                              : "repeat(3, 1fr)",
                            gap: 16,
                            marginBottom: 16,
                          }}
                        >
                          <div className="form-group">
                            <label>Type</label>
                            <select
                              value={gw.type}
                              onChange={(e) =>
                                updateGateway(gw.id, "type", e.target.value)
                              }
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                border: "2px solid #e5e7eb",
                                borderRadius: 8,
                                fontSize: 14,
                                color: "#111827",
                                backgroundColor: "#fff",
                                cursor: "pointer",
                              }}
                            >
                              {GATEWAY_TYPES.map((gt) => (
                                <option key={gt.value} value={gt.value}>
                                  {gt.symbol} {gt.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="form-group">
                            <label>Name (optional)</label>
                            <input
                              value={gw.name}
                              onChange={(e) =>
                                updateGateway(gw.id, "name", e.target.value)
                              }
                              placeholder="e.g., Is Approved?"
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                border: "2px solid #e5e7eb",
                                borderRadius: 8,
                                fontSize: 14,
                                color: "#111827",
                              }}
                            />
                          </div>

                          <div className="form-group">
                            <label>After Task/Gateway/Event</label>
                            <select
                              value={gw.afterTaskId}
                              onChange={(e) =>
                                updateGateway(
                                  gw.id,
                                  "afterTaskId",
                                  e.target.value,
                                )
                              }
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                border: "2px solid #e5e7eb",
                                borderRadius: 8,
                                fontSize: 14,
                                color: "#111827",
                                backgroundColor: "#fff",
                                cursor: "pointer",
                              }}
                            >
                              <option value="">Select task...</option>
                              {placementOptions.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.isStartEvent
                                    ? t.name
                                    : `${t.order}. ${t.name}`}
                                </option>
                              ))}
                            </select>
                          </div>

                          {showConverge && (
                            <div className="form-group">
                              <label
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 8,
                                }}
                              >
                                <span>
                                  Converge At Task (Join Point){" "}
                                  <span style={{ color: "#dc2626" }}>
                                    (required)
                                  </span>
                                </span>
                                {!gw.convergeToEnd && !gw.convergeAtTaskId && (
                                  <button
                                    type="button"
                                    className="secondary-btn sm"
                                    title="Pick the task right after where these branches naturally come back together (or End, if none continue)"
                                    onClick={() =>
                                      updateGateway(
                                        gw.id,
                                        "convergeAtTaskId",
                                        suggestConvergeTarget(gw),
                                      )
                                    }
                                  >
                                    Suggest
                                  </button>
                                )}
                              </label>
                              <select
                                value={
                                  gw.convergeToEnd
                                    ? "__END__"
                                    : gw.convergeAtTaskId || ""
                                }
                                onChange={(e) =>
                                  updateGateway(
                                    gw.id,
                                    "convergeAtTaskId",
                                    e.target.value,
                                  )
                                }
                                style={{
                                  width: "100%",
                                  padding: "10px 12px",
                                  border: "2px solid #e5e7eb",
                                  borderRadius: 8,
                                  fontSize: 14,
                                  color: "#111827",
                                  backgroundColor: "#fff",
                                  cursor: "pointer",
                                }}
                              >
                                <option value="">
                                  Select convergence task...
                                </option>
                                {convergeOptions.map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.order}. {t.name}
                                  </option>
                                ))}
                                <option value="__END__">
                                  ⏹ End Event — synchronize, then end
                                </option>
                              </select>
                              <small style={{ color: "#64748b", fontSize: 11 }}>
                                {gw.convergeToEnd
                                  ? "Branches synchronize, then flow into the single End event."
                                  : "Where all branches merge back together"}
                              </small>
                            </div>
                          )}

                          {showConverge && (
                            <div className="form-group">
                              <label>Join Gateway Label</label>
                              <input
                                value={gw.convergeGatewayName || ""}
                                onChange={(e) =>
                                  updateGateway(
                                    gw.id,
                                    "convergeGatewayName",
                                    e.target.value,
                                  )
                                }
                                placeholder="e.g., All checks done"
                                style={{
                                  width: "100%",
                                  padding: "10px 12px",
                                  border: "2px solid #e5e7eb",
                                  borderRadius: 8,
                                  fontSize: 14,
                                  color: "#111827",
                                }}
                              />
                              <small style={{ color: "#64748b", fontSize: 11 }}>
                                Label for the converging (join) gateway
                              </small>
                            </div>
                          )}
                        </div>

                        {/* Branches Section */}
                        <div
                          style={{
                            background: "#f8fafc",
                            borderRadius: 8,
                            padding: 14,
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 12,
                            }}
                          >
                            <span style={{ fontWeight: 600, color: "#334155" }}>
                              Branches
                            </span>
                            <button
                              className="secondary-btn sm"
                              onClick={() => addBranch(gw.id)}
                            >
                              + Add Branch
                            </button>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 10,
                            }}
                          >
                            {gw.branches.map((br, brIdx) => {
                              const isLoop = isLoopBranch(
                                gw.afterTaskId,
                                br.targetTaskId,
                              );
                              const isSelfLoop =
                                String(br.targetTaskId) ===
                                String(gw.afterTaskId);
                              const invalidTarget =
                                br.targetTaskId &&
                                !isValidTargetTask(br.targetTaskId);
                              const needsConditionWarning =
                                isLoop && !br.condition;

                              return (
                                <div
                                  key={br.id}
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: showConditions
                                      ? allowEndEvent
                                        ? "1fr 1fr 1fr 100px"
                                        : "1fr 1fr 100px"
                                      : allowEndEvent
                                        ? "1fr 1fr 100px"
                                        : "1fr 100px",
                                    gap: 12,
                                    alignItems: "end",
                                    padding: 12,
                                    background: isLoop ? "#fffbeb" : "#fff",
                                    borderRadius: 6,
                                    border: `1px solid ${invalidTarget ? "#ef4444" : isLoop ? "#f59e0b" : "#e5e7eb"}`,
                                  }}
                                >
                                  <div
                                    className="form-group"
                                    style={{ marginBottom: 0 }}
                                  >
                                    <label
                                      style={{
                                        fontSize: 12,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                      }}
                                    >
                                      Target Task
                                      {isLoop && (
                                        <span
                                          style={{
                                            padding: "1px 5px",
                                            background: "#f59e0b",
                                            color: "white",
                                            borderRadius: 3,
                                            fontSize: 9,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {isSelfLoop ? "SELF-LOOP" : "LOOP"}
                                        </span>
                                      )}
                                    </label>
                                    <select
                                      value={br.targetTaskId || ""}
                                      onChange={(e) =>
                                        updateBranch(
                                          gw.id,
                                          br.id,
                                          "targetTaskId",
                                          e.target.value,
                                        )
                                      }
                                      style={{
                                        width: "100%",
                                        padding: "8px 10px",
                                        border: `1px solid ${invalidTarget ? "#ef4444" : isLoop ? "#f59e0b" : "#d1d5db"}`,
                                        borderRadius: 6,
                                        fontSize: 13,
                                        color: "#111827",
                                        backgroundColor: invalidTarget
                                          ? "#fef2f2"
                                          : isLoop
                                            ? "#fffbeb"
                                            : "#fff",
                                      }}
                                    >
                                      <option value="">
                                        {allowEndEvent
                                          ? "Select task or leave empty for end event"
                                          : "Select task"}
                                      </option>
                                      {targetOptions.map((t) => {
                                        const gatewayTask = taskOptions.find(
                                          (gt) =>
                                            String(gt.id) ===
                                            String(gw.afterTaskId),
                                        );
                                        const isLoopTarget =
                                          gatewayTask &&
                                          t.order <= gatewayTask.order;
                                        return (
                                          <option key={t.id} value={t.id}>
                                            {t.order}. {t.name}{" "}
                                            {isLoopTarget ? " ← (loop)" : ""}
                                          </option>
                                        );
                                      })}
                                    </select>
                                    {invalidTarget && (
                                      <small
                                        style={{
                                          color: "#ef4444",
                                          fontSize: 10,
                                        }}
                                      >
                                        Target task not found
                                      </small>
                                    )}
                                  </div>
                                  {allowEndEvent && (
                                    <div
                                      className="form-group"
                                      style={{ marginBottom: 0 }}
                                    >
                                      <label style={{ fontSize: 12 }}>
                                        End Event Name (if no target)
                                      </label>
                                      <input
                                        value={br.endEventName || ""}
                                        onChange={(e) =>
                                          updateBranch(
                                            gw.id,
                                            br.id,
                                            "endEventName",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="e.g., Rejected, Cancelled"
                                        disabled={!!br.targetTaskId}
                                        style={{
                                          width: "100%",
                                          padding: "8px 10px",
                                          border: "1px solid #d1d5db",
                                          borderRadius: 6,
                                          fontSize: 13,
                                          color: br.targetTaskId
                                            ? "#9ca3af"
                                            : "#111827",
                                          backgroundColor: br.targetTaskId
                                            ? "#f9fafb"
                                            : "#fff",
                                        }}
                                      />
                                    </div>
                                  )}

                                  {showConditions && (
                                    <div
                                      className="form-group"
                                      style={{ marginBottom: 0 }}
                                    >
                                      <label
                                        style={{
                                          fontSize: 12,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 4,
                                        }}
                                      >
                                        Condition
                                        {needsConditionWarning && (
                                          <span
                                            style={{
                                              color: "#dc2626",
                                              fontSize: 10,
                                            }}
                                          >
                                            ⚠️
                                          </span>
                                        )}
                                      </label>
                                      <input
                                        value={br.condition || ""}
                                        onChange={(e) =>
                                          updateBranch(
                                            gw.id,
                                            br.id,
                                            "condition",
                                            e.target.value,
                                          )
                                        }
                                        placeholder={
                                          isLoop
                                            ? "Required for loops!"
                                            : "e.g., approved == true"
                                        }
                                        style={{
                                          width: "100%",
                                          padding: "8px 10px",
                                          border: `1px solid ${needsConditionWarning ? "#f59e0b" : "#d1d5db"}`,
                                          borderRadius: 6,
                                          fontSize: 13,
                                          color: "#111827",
                                          backgroundColor: needsConditionWarning
                                            ? "#fffbeb"
                                            : "#fff",
                                        }}
                                      />
                                      {needsConditionWarning && (
                                        <small
                                          style={{
                                            color: "#f59e0b",
                                            fontSize: 10,
                                          }}
                                        >
                                          Add condition to prevent infinite loop
                                        </small>
                                      )}
                                    </div>
                                  )}

                                  <div style={{ textAlign: "right" }}>
                                    <button
                                      className="danger-btn sm"
                                      onClick={() => removeBranch(gw.id, br.id)}
                                      disabled={
                                        normalizeType(gw.type) ===
                                          "EXCLUSIVE" && gw.branches.length <= 2
                                      }
                                      style={{
                                        opacity:
                                          normalizeType(gw.type) ===
                                            "EXCLUSIVE" &&
                                          gw.branches.length <= 2
                                            ? 0.5
                                            : 1,
                                      }}
                                    >
                                      ×
                                    </button>
                                  </div>

                                  {/* Connect to End Event toggle */}
                                  {br.targetTaskId &&
                                    !isLoop &&
                                    !br.endEventName &&
                                    normalizeType(gw.type) !== "PARALLEL" && (
                                      <div
                                        style={{
                                          gridColumn: "1 / -1",
                                          marginTop: 2,
                                        }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            cursor: "pointer",
                                            userSelect: "none",
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color:
                                              br.connectToEnd !== false
                                                ? "#059669"
                                                : "#6b7280",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={br.connectToEnd !== false}
                                            onChange={(e) =>
                                              updateBranch(
                                                gw.id,
                                                br.id,
                                                "connectToEnd",
                                                e.target.checked,
                                              )
                                            }
                                            style={{
                                              width: 14,
                                              height: 14,
                                              cursor: "pointer",
                                              accentColor: "#059669",
                                            }}
                                          />
                                          Connect chain to End Event
                                          <span
                                            style={{
                                              fontSize: 10,
                                              color: "#94a3b8",
                                              fontWeight: 400,
                                            }}
                                          >
                                            (last task → End)
                                          </span>
                                        </label>
                                      </div>
                                    )}
                                </div>
                              );
                            })}
                          </div>

                          {normalizeType(gw.type) === "EXCLUSIVE" &&
                            gw.branches.length < 2 && (
                              <div
                                style={{
                                  marginTop: 10,
                                  padding: 8,
                                  background: "#fee2e2",
                                  color: "#b91c1c",
                                  borderRadius: 6,
                                  fontSize: 13,
                                }}
                              >
                                Exclusive gateways require at least 2 branches.
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div
          className="modal-footer"
          style={{ display: "flex", justifyContent: "space-between", gap: 12 }}
        >
          <div>
            {(wizardMode || editGateway) &&
              gateways.length > 0 &&
              wizardStep > 1 && (
                <button
                  className="secondary-btn"
                  onClick={() => setWizardStep((s) => s - 1)}
                >
                  ← Back
                </button>
              )}
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button className="secondary-btn" onClick={onClose}>
              Cancel
            </button>
            {(wizardMode || editGateway) && gateways.length > 0 ? (
              wizardStep < 4 ? (
                <button
                  className="primary-btn"
                  onClick={() => setWizardStep((s) => s + 1)}
                  disabled={!canProceed(wizardStep)}
                >
                  Next →
                </button>
              ) : (
                <button className="primary-btn" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? "Saving…" : "✓ Apply Gateway"}
                </button>
              )
            ) : (
              <button className="primary-btn" onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Saving…" : "Apply Gateways"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GatewayConfigModal;
